import { useState, useEffect } from "react";
import axios from "axios";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FiX, FiMenu, FiHome, FiBox, FiPhone, FiBriefcase, FiSearch, FiShoppingBag, FiTrash2 } from "react-icons/fi";
import suntek from "../../assets/sun_tek_logo.png";
import { FETCH_PRODUCT_SEARCH_DATA_API, FETCH_SUBCATEGORIES_API, ENQUIRE_NOW_API } from "../Api";
import { Button, Input, Textarea } from "@material-tailwind/react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Dialog, DialogBody, DialogHeader, DialogFooter } from "@material-tailwind/react";

export default function Navbar({ categories = [] }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [products, setProducts] = useState([]);
  const [subCategories, setSubCategories] = useState({});
  const [isProductDropdownOpen, setIsProductDropdownOpen] = useState(false); // desktop hover
  const [isMobileDropdownOpen, setIsMobileDropdownOpen] = useState(false); // mobile toggle
  const [cartItems, setCartItems] = useState([]);
  const [cartCount, setCartCount] = useState(0);
  const location = useLocation();

  const navigate = useNavigate();

  useEffect(() => {
    fetchSearchData();
    categories.forEach((cat) => {
      fetchSubCategoriesFromServer(cat.id);
    });

    // ✅ Initial cart load
    const cart = localStorage.getItem("cart")
      ? JSON.parse(localStorage.getItem("cart"))
      : [];
    setCartItems(cart);
    setCartCount(cart.length);

    const handleCartUpdate = () => {
      const cart = localStorage.getItem("cart")
        ? JSON.parse(localStorage.getItem("cart"))
        : [];
      setCartItems(cart);
      setCartCount(cart.length);
    };

    window.addEventListener("cartUpdated", handleCartUpdate);
    return () => window.removeEventListener("cartUpdated", handleCartUpdate);
  }, [categories]);


  const fetchSearchData = async () => {
    try {
      const { data } = await axios.get(FETCH_PRODUCT_SEARCH_DATA_API);
      setProducts(data);
    } catch (error) {
      console.error("Error fetching product data:", error);
    }
  };

  const fetchSubCategoriesFromServer = async (categoryId) => {
    try {
      const response = await fetch(`${FETCH_SUBCATEGORIES_API}${categoryId}`);
      if (!response.ok) throw new Error("Failed to fetch sub-categories");

      const subCategoriesData = await response.json();

      // ✅ Sort sub-categories by uId ascending
      const sortedSubCategories = subCategoriesData.sort((a, b) => a.uId - b.uId);

      setSubCategories((prev) => ({
        ...prev,
        [categoryId]: sortedSubCategories,
      }));
    } catch (error) {
      console.error("Error fetching sub-categories:", error);
    }
  };


  const [isEnquiryOpen, setIsEnquiryOpen] = useState(false);
  const [formData, setFormData] = useState({
    orgName: "",
    name: "",
    phone: "",
    email: "",
    description: "",
  });

  // Open modal and pre-fill product name
  const openEnquiryModal = () => {
    setIsEnquiryOpen(true);
  };

  // Close modal
  const closeEnquiryModal = () => {
    setIsEnquiryOpen(false);
  };

  const handleChange = (e) => {
    e.preventDefault();
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };


  // Handle form submission
  const handleSubmit = async () => {
    // Validation: Check if all fields are filled
    if (
      !formData.orgName ||
      !formData.name ||
      !formData.phone ||
      !formData.email ||
      !formData.description
    ) {
      toast.error("Please fill in all fields before submitting.");
      return;
    }

    // Get cart items from localStorage
    const cartItems = localStorage.getItem("cart")
      ? JSON.parse(localStorage.getItem("cart"))
      : [];

    if (cartItems.length === 0) {
      toast.error("Your cart is empty. Please add products before submitting.");
      return;
    }

    // Prepare data to send
    const payload = {
      ...formData,
      cart: cartItems.map((item) => ({
        productName: item.product.name,
        quantity: item.qty,
        img: item.product.img?.[0] || "",
      })),
    };

    try {
      // API Call
      const response = await axios.post(ENQUIRE_NOW_API, payload, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.status !== 200) throw new Error("Failed to submit Enquiry");

      // Success Message
      toast.success(
        "Thanks for reaching out to us! Our sales team will contact you shortly."
      );

      // Clear Form
      setFormData({
        orgName: "",
        name: "",
        phone: "",
        email: "",
        description: "",
      });

      // Close Modal
      closeEnquiryModal();

      // ✅ Clear cart from localStorage
      localStorage.removeItem("cart");

      // ✅ Update cart state if you have one
      setCartItems([]);
      setCartCount(0);

      // ✅ Notify listeners/components
      window.dispatchEvent(new Event("cartUpdated"));
    } catch (error) {
      toast.error("Error submitting Enquiry. Please try again.");
      console.error("Submission Error:", error);
    }
  };



  // Decrease quantity
  const decreaseQty = (item) => {
    const newCart = cartItems.map((cartItem) =>
      cartItem.cartId === item.cartId
        ? { ...cartItem, qty: Math.max(1, cartItem.qty - 1) }
        : cartItem
    );
    setCartItems(newCart);
    localStorage.setItem("cart", JSON.stringify(newCart));
    setCartCount(newCart.length);

    // ✅ Dispatch event so other components can update
    window.dispatchEvent(new CustomEvent("cartUpdated", { detail: newCart }));
  };

  // Increase quantity
  const increaseQty = (item) => {
    const newCart = cartItems.map((cartItem) =>
      cartItem.cartId === item.cartId
        ? { ...cartItem, qty: cartItem.qty + 1 }
        : cartItem
    );
    setCartItems(newCart);
    localStorage.setItem("cart", JSON.stringify(newCart));

    window.dispatchEvent(new CustomEvent("cartUpdated", { detail: newCart }));
  };

  // Delete item
  const deleteItem = (item) => {
    const newCart = cartItems.filter((cartItem) => cartItem.cartId !== item.cartId);
    setCartItems(newCart);
    localStorage.setItem("cart", JSON.stringify(newCart));
    setCartCount(newCart.length);

    window.dispatchEvent(new CustomEvent("cartUpdated", { detail: newCart }));
  };


  const navItems = [
    { path: "/", label: "Home", icon: <FiHome /> },
    { path: "/products", label: "Products", icon: <FiBox />, dropdown: true },
    { path: "/careers", label: "Careers", icon: <FiBriefcase /> },
    { path: "/contact", label: "Contact Us", icon: <FiPhone /> },
    { path: "/", label: "Quote", icon: <FiShoppingBag /> },
  ];

  const searchProducts = products.filter((product) =>
    new RegExp(searchQuery, "i").test(product.name)
  );

  return (
    <>
      <nav
        className={`bg-white py-4 flex items-center justify-between px-4 md:px-10 absolute w-full z-50 sticky top-0`}
      >
        {/* Logo */}
        <div>
          <a href="/" className="block">
            <img src={suntek} alt="Sun Teknolozy Logo" className="h-12 rounded-lg" />
          </a>
        </div>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center space-x-6 relative">
          <ul className="flex space-x-6 text-sm font-medium">
            {navItems.map((item) => {
              // ✅ Skip highlight for Quote (modal only)
              if (item.label === "Quote") {
                return (
                  <li key={item.label} className="relative">
                    <div
                      onClick={openEnquiryModal}
                      className="flex items-center space-x-2 px-3 py-2 rounded-lg cursor-pointer text-black transition-all hover:bg-green-100 hover:text-green-600"
                    >
                      <span>{item.icon}</span>
                      <span className="relative flex items-center">
                        {item.label}
                        {cartCount > 0 && (
                          <span className="ml-1 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-red-600 rounded-full">
                            {cartCount}
                          </span>
                        )}
                      </span>
                    </div>
                  </li>
                );
              }

              // ✅ Smart highlight logic for all other menu items
              const pathname = location.pathname;

              const isActive =
                item.dropdown
                  ? pathname.startsWith("/products") // highlight all product subpaths
                  : item.path === "/"
                    ? !(
                      pathname.startsWith("/products") ||
                      pathname.startsWith("/careers") ||
                      pathname.startsWith("/contact")
                    ) // highlight Home only when not in any other section
                    : pathname.startsWith(item.path); // highlight careers, contact etc.

              return (
                <li key={item.path} className="relative">
                  {item.dropdown ? (
                    // ✅ Products Dropdown
                    <div
                      onMouseEnter={() => setIsProductDropdownOpen(true)}
                      onMouseLeave={() => setIsProductDropdownOpen(false)}
                      className={`flex items-center space-x-2 px-3 py-2 rounded-lg cursor-pointer transition-all ${isActive
                          ? "bg-green-500 text-white"
                          : "text-black hover:bg-green-100 hover:text-green-600"
                        }`}
                    >
                      <span>{item.icon}</span>
                      <span>{item.label}</span>
                    </div>
                  ) : (
                    // ✅ Normal Links (Home, Careers, Contact)
                    <Link to={item.path}>
                      <div
                        className={`flex items-center space-x-2 px-3 py-2 rounded-lg cursor-pointer transition-all ${isActive
                            ? "bg-green-500 text-white"
                            : "text-black hover:bg-green-100 hover:text-green-600"
                          }`}
                      >
                        <span>{item.icon}</span>
                        <span>{item.label}</span>
                      </div>
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>

          {/* ✅ Search Bar (visible on product pages) */}
          {location.pathname.startsWith("/products") && (
            <div className="relative ml-6">
              <input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-64 p-2 pl-10 border-2 border-black rounded-2xl bg-transparent placeholder-black text-black"
              />
              <FiSearch className="absolute top-3 left-3 text-black" />

              {searchQuery && (
                <div className="absolute top-full left-0 w-full bg-white border border-gray-300 rounded-lg shadow-lg mt-1 z-50">
                  {searchProducts.length > 0 ? (
                    searchProducts.map((product) => (
                      <a
                        key={product.id}
                        href={`/products/${product.categoryId}/${product.subCategoryId}/${product.id}`}
                        className="block px-4 py-2 hover:bg-gray-100 text-black cursor-pointer"
                        onClick={() => {
                          setSearchQuery("");
                          navigate(`/products/${product.categoryId}/${product.subCategoryId}/${product.id}`);
                        }}
                      >
                        {product.name}
                      </a>
                    ))
                  ) : (
                    <p className="px-4 py-2 text-gray-500">No products found</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>


        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsOpen(true)}
          className={`md:hidden text-2xl text-green`}
        >
          <FiMenu />
        </button>

        {/* Mobile Drawer */}
        <div
          className={`fixed top-0 right-0 h-full w-3/4 bg-gray-900 shadow-xl p-6 transform transition-transform md:hidden z-50 
          ${isOpen ? "translate-x-0" : "translate-x-full"}`}
        >
          {/* Drawer Header */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-bold text-white">Menu</h2>
            <button onClick={() => setIsOpen(false)} className="text-white">
              <FiX size={24} />
            </button>
          </div>

          {/* Mobile Search */}
          <div className="relative mb-4">
            <input
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full p-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 bg-transparent placeholder-gray-600 text-white"
            />
            <FiSearch className="absolute top-3 left-3 text-gray-400" />

            {searchQuery && (
              <div className="absolute top-full left-0 w-full bg-white border border-gray-300 rounded-lg shadow-lg mt-1 z-50">
                {searchProducts.length > 0 ? (
                  searchProducts.map((product) => (
                    <a
                      key={product.id}
                      href={`/products/${product.categoryId}/${product.subCategoryId}/${product.id}`}
                      className="block px-4 py-2 hover:bg-gray-100 text-black cursor-pointer"
                      onClick={(e) => {
                        setSearchQuery("");
                        navigate(`/products/${product.categoryId}/${product.subCategoryId}/${product.id}`);
                      }}
                    >
                      {product.name}
                    </a>

                  ))
                ) : (
                  <p className="px-4 py-2 text-gray-500">No products found</p>
                )}
              </div>
            )}
          </div>

          {/* Drawer Navigation */}
          <ul className="space-y-4 text-sm font-medium">
            {navItems.map((item) => (
              <li key={item.path}>
                {item.dropdown ? (
                  <>
                    <button
                      onClick={() => setIsMobileDropdownOpen(!isMobileDropdownOpen)}
                      className={`w-full flex justify-between items-center px-3 py-2 rounded-lg text-white transition-all 
            ${location.pathname.includes("products")
                          ? "bg-green-500"
                          : "hover:bg-green-100 hover:text-green-300"
                        }`}
                    >
                      <span className="flex items-center space-x-2">
                        <span>{item.icon}</span>
                        <span>{item.label}</span>
                        {/* Badge for Quote */}
                        {item.label === "Quote" && cartCount > 0 && (
                          <span className="ml-2 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-red-600 rounded-full">
                            {cartCount}
                          </span>
                        )}
                      </span>
                      <span className="text-xs">{isMobileDropdownOpen ? "▲" : "▼"}</span>
                    </button>

                    {isMobileDropdownOpen && (
                      <ul className="mt-2 space-y-2 ml-6 border-l border-green-500 pl-4">
                        {categories.map((category) => (
                          <li key={category.id}>
                            <a
                              href={`/products/${category.id}`}
                              className="text-gray-300 hover:text-green-400 block"
                              onClick={() => {
                                setSearchQuery("");
                                setIsOpen(false);
                                setIsMobileDropdownOpen(false);
                              }}
                            >
                              {category.name}
                            </a>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                ) : item.label === "Quote" ? (
                  // ✅ Quote opens dialog instead of navigating
                  <div
                    onClick={openEnquiryModal}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-lg cursor-pointer transition-all text-white
          hover:bg-green-100 hover:text-green-300`}
                  >
                    <span>{item.icon}</span>
                    <span>{item.label}</span>
                    {cartCount > 0 && (
                      <span className="ml-2 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-red-600 rounded-full">
                        {cartCount}
                      </span>
                    )}
                  </div>
                ) : (
                  <Link to={item.path} onClick={() => setIsOpen(false)}>
                    <div
                      className={`flex items-center space-x-2 px-3 py-2 rounded-lg cursor-pointer transition-all text-white
            ${location.pathname === item.path
                          ? "bg-green-500"
                          : "hover:bg-green-100 hover:text-green-300"
                        }`}
                    >
                      <span>{item.icon}</span>
                      <span>{item.label}</span>
                    </div>
                  </Link>
                )}
              </li>
            ))}


          </ul>
        </div>

        <Dialog open={isEnquiryOpen} handler={closeEnquiryModal} size="xl">
          <DialogHeader className="text-md font-semibold bg-gray-100 p-4 rounded-t-lg">
            My Quotes
          </DialogHeader>

          <DialogBody className="p-6 max-h-[70vh] overflow-y-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left Section: FAQs */}
              <div className="border-r pr-6 space-y-4">
                <h3 className="text-lg font-semibold text-gray-700 mb-2">Request a Quote ({cartCount} Items)</h3>
                {cartItems.map((item, idx) => (
                  <div key={item.cartId} className="flex items-center gap-4 border p-3 rounded-lg">
                    {/* Product Image */}
                    <img
                      src={item.product.img?.[0]}
                      alt={item.product.name}
                      className="w-16 h-16 object-contain rounded-md"
                    />

                    {/* Name & Quantity */}
                    <div className="flex-1">
                      <a
                        href={`/products/${item.product.categoryId}/${item.product.subCategoryId}/${item.product.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-gray-900 hover:text-green-600 transition cursor-pointer"
                      >
                        {item.product.name}
                      </a>



                      {/* Quantity selector */}
                      <div className="flex items-center mt-2 gap-2">
                        <button
                          onClick={() => decreaseQty(item)}
                          className="px-2 py-1 border rounded-md bg-gray-100 hover:bg-gray-200"
                        >
                          −
                        </button>
                        <span className="px-2">{item.qty}</span>
                        <button
                          onClick={() => increaseQty(item)}
                          className="px-2 py-1 border rounded-md bg-gray-100 hover:bg-gray-200"
                        >
                          +
                        </button>
                      </div>
                    </div>

                    {/* Delete button */}
                    <button
                      onClick={() => deleteItem(item)}
                      className="text-red-500 hover:text-red-700 font-bold"
                    >
                      <FiTrash2 size={18} />
                    </button>
                  </div>
                ))}
              </div>

              {/* Right Section: Form */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-700 mb-2">Please Fill out this form, Our team will contact you soon!</h3>
                <Input
                  type="text"
                  name="orgName"
                  required
                  label="Organization Name"
                  value={formData.orgName}
                  onChange={handleChange}
                  className="border border-gray-300 rounded-md px-3 py-2 w-full text-sm"
                />
                <Input
                  type="text"
                  name="name"
                  required
                  label="Your Name"
                  value={formData.name}
                  onChange={handleChange}
                  className="border border-gray-300 rounded-md px-3 py-2 w-full text-sm"
                />
                <Input
                  type="tel"
                  required
                  name="phone"
                  label="Phone Number"
                  value={formData.phone}
                  onChange={handleChange}
                  maxLength={10}
                  pattern="\d*"
                  className="border border-gray-300 rounded-md px-3 py-2 w-full text-sm"
                />

                <Input
                  type="email"
                  name="email"
                  required
                  label="Email"
                  value={formData.email}
                  onChange={handleChange}
                  className="border border-gray-300 rounded-md px-3 py-2 w-full text-sm"
                />


                {/* Description Box */}
                <Textarea
                  name="description"
                  label="Describe your inquiry"
                  required
                  value={formData.description}
                  onChange={handleChange}
                  className="border border-gray-300 rounded-md px-3 py-2 w-full h-24 text-sm"
                />
              </div>
            </div>
          </DialogBody>

          <DialogFooter className="flex justify-end space-x-4 bg-gray-100 p-4 rounded-b-lg">
            <Button className="px-4 py-2 bg-gray-400 text-white rounded-md text-sm" onClick={closeEnquiryModal}>
              Cancel
            </Button>
            <Button className="px-4 py-2 bg-green-500 text-white rounded-md text-sm hover:bg-green-600"
              onClick={() => handleSubmit()}>
              Submit
            </Button>
          </DialogFooter>
          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop={true}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
          />
        </Dialog>
      </nav>

      {isProductDropdownOpen && (
        <>
          {/* 🔹 Transparent Blur Overlay */}
          {isProductDropdownOpen && (
            <div
              className="fixed inset-0 top-[62px] bg-black/20 backdrop-blur-sm z-40"
            />
          )}


          {/* 🔹 Dropdown Menu */}
          <div
            className="fixed inset-0 top-[60px] w-full h-[320px] bg-white shadow-lg z-50 rounded-b-2xl"
            onMouseEnter={() => setIsProductDropdownOpen(true)}
            onMouseLeave={() => setIsProductDropdownOpen(false)}
          >
            <div className="max-w-7xl mx-auto p-6">
              <ul className="flex justify-center gap-x-8">
                {categories.map((category, idx) => (
                  <li
                    key={category.id}
                    className={`px-6 ${idx !== 0 ? "border-l border-gray-300" : ""}`}
                  >
                    <a
                      href={`/products/${category.id}/`}
                      className="text-black font-semibold hover:text-green-500"
                    >
                      {category.name}
                    </a>
                    {subCategories[category.id] && (
                      <ul className="mt-2 space-y-1 text-sm text-gray-600">
                        {subCategories[category.id].map((sub) => (
                          <li key={sub.id}>
                            <a
                              href={`/products/${category.id}/${sub.id}`}
                              className="hover:text-green-500"
                            >
                              {sub.name}
                            </a>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>

        </>
      )}
    </>
  );
}
